// src/pages/league-dashboard/free-agent-search/VirtualPlayerTable.js
// M2 DESIGN — react-window virtualization + DynastyTable row styling
import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { FixedSizeList } from 'react-window';
import { RefreshCw, AlertCircle, ChevronUp, ChevronDown, ChevronsUpDown, Users } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { createDynamicColumns } from './TableColumns';
import { useBatchSelection } from './BatchSelectionProvider';
import { leaguesAPI } from '../../../services/apiService';
import DropDesignationModal from '../../../components/modals/DropDesignationModal';
import { getNestedValue } from '../../../utils/getNestedValue';
import { canClaimWaiverPlayer } from '../../../utils/RosterCapacityUtils';

// Exact colors from DynastyTable.js CSS
const SEASON_BG    = 'rgb(38,38,38)';
const ROLLING_BG   = 'rgb(23,23,23)';
const SEASON_HOVER = 'rgb(64,64,64)';
const ROLLING_HOVER= 'rgb(45,45,45)';
const ROLLING_TEXT = '#FBBF24';
const HEADER_BG    = 'rgb(28,28,28)';
const HEADER_BORDER= 'rgba(250,204,21,0.2)';

// Row heights — must match exactly what renders
const SEASON_ROW_H  = 28;
const ROLLING_ROW_H = 22;
const PAIR_HEIGHT   = SEASON_ROW_H + ROLLING_ROW_H; // 48px per player

const VirtualPlayerTable = ({ state, leagueId, isCommissionerMode, activeTeamName, activeTeamId }) => {
  const listRef = useRef(null);
  const tableContainerRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { bulkMode } = useBatchSelection();

  // Waiver claim modal state
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverPlayerPending, setWaiverPlayerPending] = useState(null);
  const [waiverError, setWaiverError] = useState('');
  const [hoveredPlayerIndex, setHoveredPlayerIndex] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: state.activeTab === 'hitting'
      ? 'stats.season.hitting.home_runs'
      : 'stats.season.pitching.strikeouts_pitched',
    direction: 'desc'
  });

  const {
    players,
    loading,
    backgroundLoading,
    hasMore,
    activeTab,
    showAll,
    handlePlayerClick,
    handleAddPlayer,
    loadMore,
    transactionsEnabled,
    addingPlayer,
    leagueStatus,
    savedPrices,
    statsYear,
    currentSeason,
    advancedFilters,
    showRecent = true,
  } = state;

  // Active stats window. Season default for the mount-race edge where
  // advancedFilters initializes before useSearchFilters' defaults populate.
  const dateRange = advancedFilters?.dateRange ?? 'season';
  // Item size depends on whether the rolling row renders. FixedSizeList needs
  // a stable height per item; we recompute when the user toggles the Recent
  // visibility control via useMemo on showRecent.
  const itemHeight = showRecent ? PAIR_HEIGHT : SEASON_ROW_H;

  // Event delegation — handles add-player-btn and claim-player-btn
  useEffect(() => {
    const handleClick = (e) => {
      const addButton = e.target.closest('.add-player-btn');
      if (addButton && !addButton.disabled) {
        e.preventDefault();
        e.stopPropagation();
        const playerDataStr = addButton.getAttribute('data-player');
        if (playerDataStr) {
          try {
            const { player, leaguePlayerId, canAddPlayer, loading } = JSON.parse(playerDataStr);
            if (state.handleAddPlayer && leaguePlayerId && canAddPlayer && !loading) {
              state.handleAddPlayer(player);
            }
          } catch (err) { console.error('Error parsing player data:', err); }
        }
        return;
      }
      const claimButton = e.target.closest('.claim-player-btn');
      if (claimButton && !claimButton.disabled) {
        e.preventDefault();
        e.stopPropagation();
        const playerDataStr = claimButton.getAttribute('data-player');
        if (playerDataStr) {
          try {
            const { player, leaguePlayerId, canAddPlayer, loading } = JSON.parse(playerDataStr);
            if (leaguePlayerId && canAddPlayer && !loading) {
              setWaiverPlayerPending(player);
              setShowWaiverModal(true);
            }
          } catch (err) { console.error('Error parsing player data for waiver claim:', err); }
        }
      }
    };
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('click', handleClick, true);
      return () => container.removeEventListener('click', handleClick, true);
    }
  }, [state.handleAddPlayer]);

  const handleWaiverClaimConfirm = async (dropPlayerId) => {
    if (!waiverPlayerPending) return;
    setShowWaiverModal(false);
    setWaiverError('');
    const leaguePlayerId = waiverPlayerPending.ids?.league_player;
    if (!leaguePlayerId) { setWaiverError('Cannot claim: player has no league_player id.'); return; }
    try {
      const res = await leaguesAPI.submitWaiverClaim(leagueId, {
        player_league_id: leaguePlayerId,
        drop_player_league_id: dropPlayerId || null,
      });
      const data = res?.data || res;
      if (data?.success) {
        const playerName = waiverPlayerPending.info?.full_name || 'player';
        if (state.setSuccessMessage) {
          state.setSuccessMessage(`Waiver claim submitted for ${playerName}.`);
          setTimeout(() => state.setSuccessMessage && state.setSuccessMessage(''), 5000);
        }
      } else {
        const msg = data?.message || data?.error || 'Failed to submit waiver claim.';
        if (state.setError) { state.setError(msg); setTimeout(() => state.setError && state.setError(''), 6000); }
        else setWaiverError(msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to submit waiver claim.';
      if (state.setError) { state.setError(msg); setTimeout(() => state.setError && state.setError(''), 6000); }
      else setWaiverError(msg);
    } finally { setWaiverPlayerPending(null); }
  };

  // Columns
  const columns = useMemo(() => createDynamicColumns({
    showAll, activeTab,
    handlePlayerClick: state.handlePlayerClick,
    handleAddPlayer: state.handleAddPlayer,
    transactionsEnabled, addingPlayer, loading,
    isCommissionerMode, activeTeamName, activeTeamId, bulkMode,
    savedPrices, leagueStatus, statsYear, currentSeason,
    leagueId, currentRoster: state.rosterCapacityAnalysis?.players || [],
    dateRange,
  }), [showAll, activeTab, state.handlePlayerClick, state.handleAddPlayer,
      transactionsEnabled, addingPlayer, loading, isCommissionerMode,
      activeTeamName, activeTeamId, bulkMode, savedPrices, leagueStatus, statsYear,
      currentSeason, leagueId, state.rosterCapacityAnalysis, dateRange]);


  // Sort handler
  const handleSort = useCallback((columnKey) => {
    setSortConfig(prev => {
      if (prev?.key === columnKey) {
        return prev.direction === 'desc'
          ? { key: columnKey, direction: 'asc' }
          : null;
      }
      return { key: columnKey, direction: 'desc' };
    });
  }, []);

  const getSortIcon = (columnKey) => {
    if (!sortConfig || sortConfig.key !== columnKey)
      return <ChevronsUpDown className={`w-3 h-3 opacity-0 group-hover:opacity-50 ${dynastyTheme.classes.text.neutralLight}`} />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className={`w-3 h-3 ${dynastyTheme.classes.text.primary}`} />
      : <ChevronDown className={`w-3 h-3 ${dynastyTheme.classes.text.primary}`} />;
  };

  // Sorted players (sort at player level, not row level)
  const sortedPlayers = useMemo(() => {
    if (!sortConfig || !players.length) return players;
    const column = columns.find(col => col.key === sortConfig.key);
    return [...players].sort((a, b) => {
      let aValue, bValue;
      if (column?.sortValue) {
        aValue = column.sortValue(a, 'season');
        bValue = column.sortValue(b, 'season');
      } else {
        aValue = getNestedValue(a, sortConfig.key);
        bValue = getNestedValue(b, sortConfig.key);
      }
      if (aValue === bValue) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (typeof aValue === 'number' && typeof bValue === 'number')
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      const aNum = parseFloat(aValue), bNum = parseFloat(bValue);
      if (!isNaN(aNum) && !isNaN(bNum))
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      return sortConfig.direction === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [players, sortConfig, columns]);

  // Base width from column definitions only — no overrides, no scaling.
  // Used to compute effectiveWidth without circular dependency.
  const baseTableWidth = useMemo(() =>
    columns.reduce((total, col) => total + (parseInt(col.width) || 80), 0),
  [columns]);

  // effectiveWidth fills the container; scaleFactor expands unresized columns proportionally.
  const effectiveWidth = containerWidth > 0 ? Math.max(containerWidth, baseTableWidth) : baseTableWidth;
  const scaleFactor = baseTableWidth > 0 ? effectiveWidth / baseTableWidth : 1;

  // getColumnWidth: manual overrides win; unoverridden columns scale with scaleFactor.
  const getColumnWidth = useCallback((columnKey) => {
    if (columnWidths[columnKey] !== undefined) return columnWidths[columnKey];
    const col = columns.find(c => c.key === columnKey);
    const baseWidth = col ? (parseInt(col.width) || 80) : 80;
    return Math.floor(baseWidth * scaleFactor);
  }, [columnWidths, columns, scaleFactor]);

  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = getColumnWidth(columnKey);
    const handleMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const diff = e.clientX - startX;
      const newWidth = Math.max(25, startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [getColumnWidth]);

  // Rendered table width — may exceed effectiveWidth if user resizes a column wider.
  const tableWidth = useMemo(() =>
    columns.reduce((total, col) => total + getColumnWidth(col.key), 0),
  [columns, getColumnWidth]);

  // listWidth: what FixedSizeList and rows actually render at.
  const listWidth = Math.max(effectiveWidth, tableWidth);
  const listHeight = Math.min(800, sortedPlayers.length * itemHeight);

  // Measure container width via ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Reset scroll on tab/filter change
  useEffect(() => {
    if (listRef.current) listRef.current.scrollToItem(0);
  }, [activeTab, state.positionFilter]);

  // Each item = one player = PAIR_HEIGHT (season row + rolling row stacked)
  const Row = useCallback(({ index, style }) => {
    const player = sortedPlayers[index];
    if (!player) return null;

    const isHovered = hoveredPlayerIndex === index;
    const hasOP = (player.league_context?.financial?.contract_type || '').startsWith('OP');

    const cellPaddingStyle = { padding: '4px 4px', fontSize: 12, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };

    const renderCell = (column, rowType) => {
      const cellValue = column.render
        ? column.render(player, rowType, null)
        : (getNestedValue(player, column.key) ?? '-');
      const width = getColumnWidth(column.key);
      const align = column.textAlign === 'text-left' ? 'left' : column.textAlign === 'text-right' ? 'right' : 'center';
      return (
        <div key={`${column.key}-${rowType}`}
          style={{ width, minWidth: width, maxWidth: width, flexShrink: 0, textAlign: align, display: 'flex', alignItems: 'center', justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', ...cellPaddingStyle }}>
          {cellValue}
        </div>
      );
    };

    return (
      <div style={{ ...style, width: listWidth }}>
        {/* Season row */}
        <div
          style={{
            display: 'flex', width: listWidth, height: SEASON_ROW_H,
            background: isHovered ? SEASON_HOVER : SEASON_BG,
            color: 'white',
            borderBottom: '1px solid rgba(250,204,21,0.07)',
            borderLeft: hasOP ? '2px solid rgba(239,68,68,0.4)' : 'none',
          }}
          onMouseEnter={() => setHoveredPlayerIndex(index)}
          onMouseLeave={() => setHoveredPlayerIndex(null)}
        >
          {columns.map(col => renderCell(col, 'season'))}
        </div>
        {/* Rolling/comparison row — visibility controlled by the Recent toggle. */}
        {showRecent && (
          <div
            style={{
              display: 'flex', width: listWidth, height: ROLLING_ROW_H,
              background: isHovered ? ROLLING_HOVER : ROLLING_BG,
              color: ROLLING_TEXT,
              fontStyle: 'italic',
              borderBottom: '1px solid rgba(250,204,21,0.15)',
              borderLeft: hasOP ? '2px solid rgba(239,68,68,0.2)' : 'none',
            }}
            onMouseEnter={() => setHoveredPlayerIndex(index)}
            onMouseLeave={() => setHoveredPlayerIndex(null)}
          >
            {columns.map(col => renderCell(col, 'rolling'))}
          </div>
        )}
      </div>
    );
  }, [sortedPlayers, columns, listWidth, getColumnWidth, hoveredPlayerIndex, showRecent]);

  // Sticky header
  const TableHeader = ({ listWidth }) => (
    <div style={{
      display: 'flex',
      width: listWidth,
      background: HEADER_BG,
      borderBottom: `1px solid ${HEADER_BORDER}`,
    }}>
      {columns.map((column) => {
        const width = getColumnWidth(column.key);
        const isSortable = column.sortable !== false;
        const isActive = sortConfig?.key === column.key;
        return (
          <div key={column.key}
            onClick={() => isSortable && handleSort(column.key)}
            onDoubleClick={() => {
              setColumnWidths(prev => {
                const next = { ...prev };
                delete next[column.key];
                return next;
              });
            }}
            style={{
              position: 'relative',
              width, minWidth: width, maxWidth: width, flexShrink: 0,
              padding: '2px 4px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              cursor: isSortable ? 'pointer' : 'default',
              color: isActive ? '#eab308' : '#6b7280',
              background: isActive ? 'rgba(250,204,21,0.08)' : 'transparent',
              userSelect: 'none',
            }}
            className={isSortable ? 'group hover:bg-gray-700/50' : ''}
          >
            <span className="truncate">{column.title}</span>
            {isSortable && <div className="ml-0.5 flex-shrink-0">{getSortIcon(column.key)}</div>}
            <div
              style={{
                position: 'absolute', right: 0, top: 0, bottom: 0, width: 4,
                cursor: 'col-resize',
                background: isResizing ? '#D4AF37' : 'transparent',
              }}
              className="hover:bg-yellow-400/70 transition-colors z-30"
              onMouseDown={(e) => handleMouseDown(e, column.key)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {waiverError && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 text-sm">{waiverError}</p>
        </div>
      )}

      <div ref={tableContainerRef} className="relative">
        <div ref={containerRef} style={{ width: '100%' }}>
          <div
            style={{ overflowX: containerWidth > 0 && listWidth > containerWidth ? 'auto' : 'hidden', background: SEASON_BG }}
            className="rounded-md shadow-lg scrollbar-thin scrollbar-thumb-yellow-400 scrollbar-track-gray-800"
          >
            <TableHeader listWidth={listWidth} />
            <FixedSizeList
              ref={listRef}
              height={listHeight}
              itemCount={sortedPlayers.length}
              itemSize={itemHeight}
              width={listWidth}
              overscanCount={10}
            >
              {Row}
            </FixedSizeList>
          </div>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg z-50">
            <div className={`flex items-center gap-3 ${dynastyTheme.components.card.base} px-6 py-4 rounded-lg`}>
              <RefreshCw className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary}`} />
              <span className={dynastyTheme.classes.text.white}>
                Loading {showAll ? 'all players' : 'free agents'}...
              </span>
            </div>
          </div>
        )}
      </div>

      {!loading && players.length === 0 && (
        <div className={`${dynastyTheme.components.card.base} p-8`}>
          <div className="text-center">
            <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralLight}`} />
            <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>No {activeTab} Found</h3>
            <p className={dynastyTheme.classes.text.neutralLight}>Try adjusting your search filters.</p>
          </div>
        </div>
      )}

      {hasMore && players.length > 0 && !state.allDataLoaded && (
        <div className="flex justify-center mt-6">
          <button onClick={loadMore} disabled={backgroundLoading}
            className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'lg')} flex items-center gap-2 disabled:opacity-50`}>
            {backgroundLoading
              ? <><RefreshCw className="w-4 h-4 animate-spin" />Loading more players...</>
              : <><Users className="w-4 h-4" />Load More Players</>}
          </button>
        </div>
      )}

      {backgroundLoading && (
        <div className="flex justify-center mt-4">
          <div className={`flex items-center gap-2 ${dynastyTheme.classes.text.neutralLight} text-sm`}>
            <RefreshCw className="w-3 h-3 animate-spin" />Loading more players in background...
          </div>
        </div>
      )}

      {(() => {
        const claim = waiverPlayerPending && state.rosterCapacityAnalysis
          ? canClaimWaiverPlayer(waiverPlayerPending, state.rosterCapacityAnalysis)
          : null;
        const handleRequestPlan = state.onWaiverRequestPlan
          ? () => {
              const player = waiverPlayerPending;
              setShowWaiverModal(false);
              setWaiverPlayerPending(null);
              state.onWaiverRequestPlan(player);
            }
          : null;
        return (
          <DropDesignationModal
            isOpen={showWaiverModal}
            onClose={() => { setShowWaiverModal(false); setWaiverPlayerPending(null); }}
            onConfirm={handleWaiverClaimConfirm}
            onRequestPlan={handleRequestPlan}
            playerBeingAdded={waiverPlayerPending}
            currentRoster={state.rosterCapacityAnalysis?.players || []}
            leagueId={leagueId}
            context="waiver_claim"
            isRosterFull={claim ? !claim.canClaim : true}
            hasActiveSlot={claim ? claim.hasActiveSlot : null}
            hasBenchSlot={claim ? claim.hasBenchSlot : null}
            waiverPriority={null} totalTeams={null}
            waiverExpiresAt={waiverPlayerPending?.league_context?.availability?.waiver_expires_at || null}
          />
        );
      })()}
    </>
  );
};

export default VirtualPlayerTable;
